import { useEffect, useState } from "react";
import { useLocation } from "@tanstack/react-router";
import { BellRing, X } from "lucide-react";
import { api } from "@/lib/api";
import {
  ACTIVE_BEFORE_ASK_MS,
  dismissed,
  mayAsk,
  readNudgeState,
  writeNudgeState,
} from "@/lib/desktop-nudge";
import { requestDesktopPermission, useDesktopPermission } from "@/lib/desktop-notifications";
import { Feature, useSession } from "@/components/session-provider";

/**
 * "Get desktop alerts?" — a bar under the header, for people who have not turned them on (D-155).
 *
 * ⚠️ **A SOFT ASK.** The browser's own permission dialog opens only from the press on Turn on —
 * see `desktop-nudge.ts` for why a page that asks unprompted costs every future user of the
 * site. This bar is the product's own copy, closes on Not now or the X, and comes back on the
 * cadence that file defines: weekly for three weeks, then monthly, per device.
 *
 * ⚠️ **NOT INVASIVE, BY SHAPE.** It pushes the page down rather than covering it, holds one
 * line, and steals no focus — the pattern Gmail and Slack settled on for the same ask. It is
 * never drawn on the profile, where the real switch is already on screen, and never on arrival:
 * it waits for the tab to have been in front for a few minutes or for a request to be opened.
 *
 * ⚠️ **A browser that has already refused is never asked**, because no page can ask again after
 * a denial. `permission === "denied"` hides the bar for good on that device; the profile's
 * switch still explains how to unblock it.
 *
 * ⚠️ Rendering only. Nothing here decides what anybody may see; the worst it can do is draw a
 * sentence somebody closes.
 */
export function DesktopNudgeBar() {
  const { user, hasFeature, refresh } = useSession();
  const { pathname } = useLocation();
  const [permission, setPermission] = useDesktopPermission();

  // Whether this session has been active for long enough, or opened a request. Component
  // state: the shell stays mounted across routes, and a reload restarting the wait is intended.
  const [active, setActive] = useState(false);
  const [state, setState] = useState(() => readNudgeState());
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  // Foreground time, counted in slices so a tab left in the background overnight does not
  // count as three minutes of attention.
  useEffect(() => {
    if (active) return;

    const SLICE_MS = 15_000;
    let seen = 0;

    const handle = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      seen += SLICE_MS;
      if (seen >= ACTIVE_BEFORE_ASK_MS) setActive(true);
    }, SLICE_MS);

    return () => window.clearInterval(handle);
  }, [active]);

  // Opening a request is the clearest sign somebody is here to work; it satisfies the wait.
  useEffect(() => {
    if (/^\/tickets\/(?!new$)[^/]+$/.test(pathname)) setActive(true);
  }, [pathname]);

  if (!hasFeature(Feature.BrowserNotifications)) return null;
  if (done || !active) return null;
  if (permission === "unsupported" || permission === "denied") return null;
  if (pathname.startsWith("/profile")) return null;

  const switchedOn = user?.desktopNotifications ?? false;

  // Already working here: the switch is on and this browser has agreed. Nothing to ask.
  if (switchedOn && permission === "granted") return null;

  if (!mayAsk(state, new Date())) return null;

  function dismiss() {
    const next = dismissed(state, new Date());
    writeNudgeState(next);
    setState(next);
    setDone(true);
  }

  async function turnOn() {
    setBusy(true);

    try {
      // ⚠️ FROM THIS PRESS AND NOWHERE ELSE — the gesture the browser requires.
      const granted = await requestDesktopPermission();
      setPermission(granted);

      if (granted !== "granted") {
        // Closed the dialog without answering, or refused it. A refusal hides the bar by the
        // permission check above; a shrug counts as a Not now.
        if (granted === "default") dismiss();
        return;
      }

      if (!switchedOn) {
        await api("/api/me/desktop-notifications", { method: "PUT", body: { enabled: true } });
        await refresh();
      }

      setDone(true);
    } catch {
      // The save failed after the browser agreed. The profile's switch is the way to retry
      // and says what went wrong there; a failure line on a nudge bar is more bar than the
      // ask deserves.
      setDone(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="region"
      aria-label="Desktop alerts"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border bg-accent/40 px-4 py-2 text-sm md:px-6"
    >
      <p className="flex min-w-0 flex-1 items-center gap-2">
        <BellRing className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span>
          {switchedOn
            ? "Allow desktop alerts in this browser, so a reply or an update reaches you while the desk is open in a tab."
            : "Get a desktop alert when someone replies or a request you are on changes, while the desk is open in a tab."}
        </span>
      </p>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void turnOn()}
          disabled={busy}
          className="h-8 rounded-lg bg-primary px-3 text-xs font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? "Asking…" : switchedOn ? "Allow" : "Turn on"}
        </button>
        <button
          type="button"
          onClick={dismiss}
          disabled={busy}
          className="h-8 rounded-lg px-2 text-xs text-muted-foreground transition hover:bg-accent hover:text-foreground"
        >
          Not now
        </button>
        <button
          type="button"
          onClick={dismiss}
          disabled={busy}
          aria-label="Dismiss"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-accent hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
